import { useCallback } from 'react';
import type { JSX } from 'react';

import { Link as RouterLink } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { Image } from '@change-corgi/design-system-legacy/components/media';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { linkResetStyles } from '@change-corgi/design-system-legacy/reset';

import { trackMovementPetitionClick } from 'src/app/pages/movement/shared/api';
import { MOVEMENT_EVENT_VIEW_CONTEXT } from 'src/app/pages/movement/shared/constants';
import type { Petition } from 'src/app/pages/movement/shared/types';

import { useMovementContext } from '../../hooks/useMovementContext';

import { MovementPetitionInfo } from './MovementPetitionInfo';
import { StatusBoxContainer } from './StatusBoxContainer';

export type PetitionCardProps = {
	petition: Petition;
	onRemove: (petitionId: string) => void;
};

export function MovementPetitionCard({
	petition: {
		id,
		slug,
		ask,
		description,
		photo,
		user,
		publishedAt,
		createdAt,
		status,
		weeklySignatureCount,
		totalSignatureCount,
	},
	onRemove,
}: PetitionCardProps): JSX.Element | null {
	const track = useTracking();
	const movementInfo = useMovementContext();
	const onClick = useCallback(() => {
		void trackMovementPetitionClick(movementInfo.id, id, MOVEMENT_EVENT_VIEW_CONTEXT, track);
	}, [id, movementInfo.id, track]);

	return (
		<RouterLink to={`/p/${encodeURIComponent(slug)}`} sx={linkResetStyles}>
			<Box variant="bordered" onClick={onClick} sx={{ cursor: 'pointer' }}>
				<StatusBoxContainer
					isVictory={status === 'VICTORY'}
					owner={user}
					publishedAt={publishedAt || createdAt}
					weeklySignatureCount={weeklySignatureCount}
					onDelete={() => onRemove(id)}
				/>
				<Image
					src={photo?.petitionMedium.url || 'https://static.change.org/images/default-petition-darker.gif'}
					alt=""
					data-testid="petition-card-cover-image"
					aspectRatio="16/9"
					fit="cover"
					sx={{ width: '100%' }}
				/>
				<MovementPetitionInfo
					ask={ask}
					totalSignatureCount={totalSignatureCount}
					description={description}
					user={user}
				/>
			</Box>
		</RouterLink>
	);
}
